import {
  createContext,
  forwardRef,
  type AnchorHTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

type NavigateOptions = {
  replace?: boolean;
};

type RouterValue = {
  pathname: string;
  navigate: (to: string, options?: NavigateOptions) => void;
};

const RouterContext = createContext<RouterValue | null>(null);

function currentPath() {
  return window.location.pathname.replace(/\/+$/, "") || "/";
}

export function RouterProvider({ children }: PropsWithChildren) {
  const [pathname, setPathname] = useState(currentPath);

  useEffect(() => {
    const update = () => setPathname(currentPath());
    window.addEventListener("popstate", update);
    return () => window.removeEventListener("popstate", update);
  }, []);

  const value = useMemo<RouterValue>(
    () => ({
      pathname,
      navigate(to, options) {
        const next = to.replace(/\/+$/, "") || "/";
        if (options?.replace) window.history.replaceState(null, "", next);
        else window.history.pushState(null, "", next);
        setPathname(next);
        window.scrollTo({ top: 0, behavior: "instant" });
      },
    }),
    [pathname],
  );

  return <RouterContext.Provider value={value}>{children}</RouterContext.Provider>;
}

export function useRouter() {
  const context = useContext(RouterContext);
  if (!context) throw new Error("useRouter deve ser usado dentro do RouterProvider.");
  return context;
}

export const Link = forwardRef<
  HTMLAnchorElement,
  AnchorHTMLAttributes<HTMLAnchorElement> & { to: string; replace?: boolean }
>(function Link({ to, replace, onClick, target, ...props }, ref) {
  const { navigate } = useRouter();

  function follow(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (
      event.defaultPrevented ||
      event.button !== 0 ||
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      target === "_blank"
    ) {
      return;
    }
    event.preventDefault();
    navigate(to, { replace });
  }

  return <a ref={ref} href={to} target={target} onClick={follow} {...props} />;
});

export function NavLink({
  to,
  end = false,
  className,
  ...props
}: Omit<React.ComponentProps<typeof Link>, "className"> & {
  end?: boolean;
  className?: string | ((options: { isActive: boolean }) => string);
}) {
  const { pathname } = useRouter();
  const isActive = end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`);
  return (
    <Link
      to={to}
      className={typeof className === "function" ? className({ isActive }) : className}
      {...props}
    />
  );
}

export function Redirect({ to }: { to: string }) {
  const { navigate } = useRouter();
  useEffect(() => navigate(to, { replace: true }), [navigate, to]);
  return null;
}
